import { Building2, MapPin, ArrowLeft, Trash2, Calendar, FileText, Hash, Globe, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import { companyService } from '../utils/companyService'
import { formatSicCodesList } from '../utils/sicCodes'
import { formatJurisdiction } from '../utils/formatters'

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [company, setCompany] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (id) {
      fetchCompany()
    }
  }, [id])

  const fetchCompany = async () => {
    if (!id) return

    try {
      setIsLoading(true)
      setError(null)
      const response = await companyService.getCompanyById(id)

      if (response.statusCode === 200 && response.response) {
        const data: any = response.response
        const companyData = data.success && data.data ? data.data : data
        setCompany(companyData)
      }
    } catch (err: any) {
      console.error('Error fetching company:', err)
      setError('Failed to load company details')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    try {
      setIsDeleting(true)
      setError(null)
      const response = await companyService.deleteCompany(id)

      if (response.statusCode === 200) {
        navigate('/companies')
      } else {
        setError('Failed to delete company. Please try again.')
        setShowDeleteConfirm(false)
      }
    } catch (err: any) {
      console.error('Error deleting company:', err)
      setError(err.message || 'Failed to delete company. Please try again.')
      setShowDeleteConfirm(false)
    } finally {
      setIsDeleting(false)
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })

  const address = company?.registeredOffice
  const addressLines = address
    ? [
        address.line1 || address.addressLine1,
        address.line2 || address.addressLine2,
        address.city || address.locality,
        address.county || address.region,
        address.postcode || address.postalCode,
        address.country,
      ].filter(Boolean)
    : []

  const confirmationStatement = company?.confirmationStatement || company?.confirmation_statement
  const csNextDate = confirmationStatement?.nextMadeUpTo || confirmationStatement?.next_made_up_to
  const csNextDue = confirmationStatement?.nextDue || confirmationStatement?.next_due
  const csLastDate = confirmationStatement?.lastMadeUpTo || confirmationStatement?.last_made_up_to

  return (
    <AuthenticatedLayout activeTab='companies'>
      <div className='mx-auto space-y-6'>
        {/* Back Button */}
        <button
          onClick={() => navigate('/companies')}
          className='flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm'
        >
          <ArrowLeft className='w-4 h-4' />
          Back to Companies
        </button>

        {isLoading ? (
          <div className='flex items-center justify-center py-20'>
            <div className='text-center'>
              <div className='animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-[#001B38] mx-auto' />
              <p className='mt-4 text-sm text-gray-500'>Loading company details...</p>
            </div>
          </div>
        ) : error ? (
          <div className='bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm'>
            {error}
          </div>
        ) : company ? (
          <>
            {/* ── Header Card ── */}
            <div className='bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden'>
              {/* Top accent bar */}
              <div className='h-1 bg-[#001B38]' />

              <div className='p-6 sm:p-8'>
                <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4'>
                  {/* Company identity */}
                  <div className='flex items-start gap-4'>
                    <div className='p-3 bg-[#001B38] rounded-xl shrink-0'>
                      <Building2 className='w-7 h-7 text-white' />
                    </div>
                    <div>
                      <h1 className='text-xl sm:text-2xl font-bold text-gray-900 leading-tight'>
                        {company.name || company.companyName}
                      </h1>
                      <p className='text-sm text-gray-500 mt-1'>
                        Company Number: <span className='font-medium text-gray-700'>{company.companyNumber}</span>
                      </p>
                      <div className='flex items-center gap-3 mt-3 flex-wrap'>
                        {company.companyStatus && (
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                              company.companyStatus === 'active'
                                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                                : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200'
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${company.companyStatus === 'active' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                            {company.companyStatus.charAt(0).toUpperCase() + company.companyStatus.slice(1)}
                          </span>
                        )}
                        {company.companyType && (
                          <span className='text-xs text-gray-500'>
                            {company.companyType}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Primary action */}
                  <button
                    onClick={() => navigate(`/ct600-filing?companyId=${company._id}`)}
                    className='flex items-center gap-2 bg-[#001B38] text-white px-5 py-2.5 rounded-lg hover:bg-[#002a50] transition-colors text-sm font-medium shrink-0 self-start'
                  >
                    <FileText className='w-4 h-4' />
                    Create CT600 Filing
                    <ChevronRight className='w-4 h-4' />
                  </button>
                </div>
              </div>
            </div>

            {/* ── Info Grid ── */}
            <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
              {/* Company Information */}
              <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-6'>
                <div className='flex items-center gap-2 mb-4'>
                  <Hash className='w-4 h-4 text-gray-400' />
                  <h3 className='text-sm font-semibold text-gray-900'>Company Information</h3>
                </div>
                <dl className='space-y-3'>
                  <div>
                    <dt className='text-xs text-gray-500'>Type</dt>
                    <dd className='text-sm font-medium text-gray-900 mt-0.5'>
                      {company.companyType || 'Private limited Company'}
                    </dd>
                  </div>
                  {company.incorporationDate && (
                    <div>
                      <dt className='text-xs text-gray-500'>Incorporated</dt>
                      <dd className='text-sm font-medium text-gray-900 mt-0.5'>
                        {formatDate(company.incorporationDate)}
                      </dd>
                    </div>
                  )}
                  {company.jurisdiction && (
                    <div>
                      <dt className='text-xs text-gray-500'>Jurisdiction</dt>
                      <dd className='text-sm font-medium text-gray-900 mt-0.5'>
                        {formatJurisdiction(company.jurisdiction)}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Registered Office */}
              <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-6'>
                <div className='flex items-center gap-2 mb-4'>
                  <MapPin className='w-4 h-4 text-gray-400' />
                  <h3 className='text-sm font-semibold text-gray-900'>Registered Office</h3>
                </div>
                {addressLines.length > 0 ? (
                  <div className='space-y-0.5'>
                    {addressLines.map((line, i) => (
                      <p key={i} className='text-sm text-gray-700'>{line}</p>
                    ))}
                  </div>
                ) : (
                  <p className='text-sm text-gray-400'>No address available</p>
                )}
              </div>

              {/* SIC Codes */}
              <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-6'>
                <div className='flex items-center gap-2 mb-4'>
                  <Globe className='w-4 h-4 text-gray-400' />
                  <h3 className='text-sm font-semibold text-gray-900'>SIC Codes</h3>
                </div>
                {company.sicCodes && company.sicCodes.length > 0 ? (
                  <div className='space-y-2'>
                    {formatSicCodesList(company.sicCodes).map((formattedCode: string, index: number) => (
                      <p key={index} className='text-sm text-gray-700'>{formattedCode}</p>
                    ))}
                  </div>
                ) : (
                  <p className='text-sm text-gray-400'>No SIC codes</p>
                )}
              </div>
            </div>

            {/* ── Accounts & Confirmation Statement ── */}
            {(company.accountingPeriod || confirmationStatement) && (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {/* Accounts */}
                {company.accountingPeriod && (
                  <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-6'>
                    <div className='flex items-center gap-2 mb-4'>
                      <Calendar className='w-4 h-4 text-gray-400' />
                      <h3 className='text-sm font-semibold text-gray-900'>Accounts</h3>
                    </div>
                    <div className='space-y-3'>
                      {company.accountingPeriod.nextMadeUpTo ? (
                        <div>
                          <p className='text-sm text-gray-700'>
                            Next accounts made up to{' '}
                            <span className='font-semibold text-gray-900'>
                              {formatDate(company.accountingPeriod.nextMadeUpTo)}
                            </span>
                          </p>
                          {company.accountingPeriod.nextDue && (
                            <p className='text-sm text-gray-500 mt-0.5'>
                              due by{' '}
                              <span className='font-medium text-gray-700'>
                                {formatDate(company.accountingPeriod.nextDue)}
                              </span>
                            </p>
                          )}
                        </div>
                      ) : (company.accountingPeriod.endDate || company.accountingPeriod.end) ? (
                        <div>
                          <p className='text-sm text-gray-700'>
                            Next accounts made up to{' '}
                            <span className='font-semibold text-gray-900'>
                              {formatDate(company.accountingPeriod.endDate || company.accountingPeriod.end)}
                            </span>
                          </p>
                          {(company.accountingPeriod.startDate || company.accountingPeriod.start) && (
                            <p className='text-sm text-gray-500 mt-0.5'>
                              due by{' '}
                              <span className='font-medium text-gray-700'>
                                {formatDate(company.accountingPeriod.startDate || company.accountingPeriod.start)}
                              </span>
                            </p>
                          )}
                        </div>
                      ) : null}
                      {company.accountingPeriod.lastMadeUpTo && (
                        <p className='text-sm text-gray-500'>
                          Last accounts made up to{' '}
                          <span className='font-medium text-gray-700'>
                            {formatDate(company.accountingPeriod.lastMadeUpTo)}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Confirmation Statement */}
                {confirmationStatement && (
                  <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-6'>
                    <div className='flex items-center gap-2 mb-4'>
                      <FileText className='w-4 h-4 text-gray-400' />
                      <h3 className='text-sm font-semibold text-gray-900'>Confirmation Statement</h3>
                    </div>
                    <div className='space-y-3'>
                      {csNextDate && (
                        <div>
                          <p className='text-sm text-gray-700'>
                            Next statement date{' '}
                            <span className='font-semibold text-gray-900'>
                              {formatDate(csNextDate)}
                            </span>
                          </p>
                          {csNextDue && (
                            <p className='text-sm text-gray-500 mt-0.5'>
                              due by{' '}
                              <span className='font-medium text-gray-700'>
                                {formatDate(csNextDue)}
                              </span>
                            </p>
                          )}
                        </div>
                      )}
                      {csLastDate && (
                        <p className='text-sm text-gray-500'>
                          Last statement dated{' '}
                          <span className='font-medium text-gray-700'>
                            {formatDate(csLastDate)}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Danger Zone ── */}
            <div className='bg-white rounded-2xl border border-red-100 shadow-sm p-6'>
              <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
                <div>
                  <h3 className='text-sm font-semibold text-gray-900'>Delete this company</h3>
                  <p className='text-xs text-gray-500 mt-0.5'>
                    Once deleted, this company and its data cannot be recovered.
                  </p>
                </div>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className='flex items-center gap-2 border border-red-200 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium shrink-0'
                >
                  <Trash2 className='w-4 h-4' />
                  Delete Company
                </button>
              </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
              <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
                <div className='bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl text-start'>
                  <h3 className='text-lg font-semibold text-gray-900 mb-2 text-start'>
                    Delete Company
                  </h3>
                  <p className='text-sm text-gray-600 mb-6'>
                    Are you sure you want to delete <strong>{company.name || company.companyName}</strong>? This action cannot be undone.
                  </p>
                  <div className='flex gap-3 justify-end'>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className='px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm'
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm'
                    >
                      {isDeleting ? (
                        <>
                          <div className='animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white' />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className='w-4 h-4' />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className='text-center py-20'>
            <Building2 className='w-10 h-10 text-gray-300 mx-auto mb-3' />
            <p className='text-sm text-gray-500'>Company not found</p>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  )
}
